import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  ThemeProvider,
} from "@mui/material";
import { Add, FilterListOff, Preview } from "@mui/icons-material";
import {
  DataGridPro as DataGrid,
  getGridDateOperators,
  getGridSingleSelectOperators,
  getGridStringOperators,
  gridClasses,
  GridColDef,
  GridFilterModel,
  GridLogicOperator,
  GridPaginationModel,
  GridRowHeightParams,
  GridSortDirection,
  GridSortModel,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { useCallback, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { useListTotalRewardsPerks } from "src/Pages/Company/TotalReward/api/usePerks";
import { useListTotalRewardsPerksCategoriesOptions } from "src/Pages/Company/TotalReward/api/usePerksCategories";
import useCompanyEmployeeGroups from "src/Pages/Company/TotalReward/api/useCompanyEmployeeGroups";
import useCompanyOrganizationUnits from "src/Pages/Company/TotalReward/api/useCompanyOrganizationUnits";
import useCompanyRentableGroups from "src/Pages/Company/TotalReward/api/useCompanyRentableGroups";
import { TotalRewardsPerk } from "src/Pages/Company/TotalReward/models/TotalRewardsPerk.models";

import { BusinessId } from "src/Components/DataTable/businessIdColumn";
import MultiSelectFilter from "src/Components/DataGrid/MultiSelectFilter";
import mapValueToOptions from "src/Components/DataGrid/mapValueToOptions";
import Pagination from "src/Components/DataGrid/Pagination";
import { theme as DefaultTheme } from "src/theme/theme";

import {
  totalRewardPerksRead,
  totalRewardPerksWrite,
} from "src/utils/RoleBasedSecurity/permissions";
import useHasPermission from "src/utils/hooks/security/useHasPermission";
import { getLanguage } from "src/utils/Languages/LanguageContext";
import __ from "src/utils/Translations";
import ExportPerks from "./ExportPerks";
import RowActionDeletePerk from "./RowActionDeletePerk";

interface PerksListingProps {
  pathPrefix: string;
  availableBenefitsEnabled: boolean;
  yourBenefitsEnabled: boolean;
}

const PerksListing = ({
  pathPrefix,
  availableBenefitsEnabled,
  yourBenefitsEnabled,
}: PerksListingProps) => {
  const canExport = useHasPermission(totalRewardPerksRead);
  const canCreate = useHasPermission(totalRewardPerksWrite);
  const canEdit = useHasPermission(totalRewardPerksWrite);
  const canDelete = useHasPermission(totalRewardPerksWrite);

  const companyId = useCompanyId();
  const { data: perkCategoriesOptions } =
    useListTotalRewardsPerksCategoriesOptions(companyId);
  const { data: employeeGroups } = useCompanyEmployeeGroups(
    companyId,
    !!availableBenefitsEnabled
  );
  const { data: rentableGroups } = useCompanyRentableGroups(
    companyId,
    !!availableBenefitsEnabled
  );
  const { data: organizationUnits } = useCompanyOrganizationUnits(
    companyId,
    !!availableBenefitsEnabled
  );

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
    logicOperator: GridLogicOperator.And,
  });

  const stringFilterOperators = getGridStringOperators().filter((operator) =>
    ["contains"].includes(operator.value)
  );

  const selectFilterOperators = getGridSingleSelectOperators().filter(
    (operator) => ["is"].includes(operator.value)
  );
  const dateFilterOperators = getGridDateOperators().filter((operator) =>
    ["is", "after", "onOrAfter", "before", "onOrBefore"].includes(
      operator.value
    )
  );

  const filters = useMemo(() => {
    const modifiedFilters = {
      tenant: companyId,
    } as { [key: string]: number | string | string[] };

    filterModel?.items.forEach((filter) => {
      if (
        ["activeFrom", "activeTo"].includes(filter.field) &&
        filter.operator !== "is"
      ) {
        if (filter.operator === "after") {
          modifiedFilters[`${filter.field}[strictly_after]`] = filter.value;
        }
        if (filter.operator === "onOrAfter") {
          modifiedFilters[`${filter.field}[after]`] = filter.value;
        }
        if (filter.operator === "before") {
          modifiedFilters[`${filter.field}[strictly_before]`] = filter.value;
        }
        if (filter.operator === "onOrBefore") {
          modifiedFilters[`${filter.field}[before]`] = filter.value;
        }
      } else if (Array.isArray(filter.value) && filter.operator === "isAnyOf") {
        modifiedFilters[filter.field] = filter.value.map(
          ({ value }) => value
        ) as string[];
      } else if (filter.value !== null) {
        modifiedFilters[filter.field] = filter.value;
      }
    });

    return modifiedFilters;
  }, [companyId, filterModel?.items]);

  const { data, isFetching, isError, error } = useListTotalRewardsPerks({
    filters,
    page: paginationModel.page + 1,
    itemsPerPage: paginationModel.pageSize,
    order: sortModel?.reduce(
      (order, { field, sort }) => ({ ...order, [field]: sort }),
      {} as { [key: string]: GridSortDirection }
    ),
  });

  const rowCountRef = useRef(data?.totalItems || 0);
  const rowCount = useMemo(() => {
    if (data?.totalItems !== undefined) {
      rowCountRef.current = data.totalItems;
    }
    return rowCountRef.current;
  }, [data?.totalItems]);

  const columns = useMemo(
    () =>
      (
        [
          {
            headerName: __("ID"),
            field: "id",
            type: "string",
            filterOperators: stringFilterOperators,
            renderCell: ({ value }) => <BusinessId value={value} />,
            width: 100,
          },
          {
            headerName: __("Nazwa"),
            field: "title",
            type: "string",
            filterOperators: stringFilterOperators,
            width: 200,
          },
          yourBenefitsEnabled && {
            headerName: __(`Widoczność: "Twoje benefity"`),
            field: "status",
            type: "boolean",
            width: 250,
          },
          availableBenefitsEnabled && {
            headerName: __(`Widoczność: "Dostępne benefity"`),
            field: "visibility",
            type: "boolean",
            width: 250,
          },
          availableBenefitsEnabled && {
            headerName: __("Data aktywności od"),
            field: "activeFrom",
            valueGetter: (value) => value && new Date(value),
            type: "date",
            filterOperators: dateFilterOperators,
            width: 180,
          },
          availableBenefitsEnabled && {
            headerName: __("Data aktywności do"),
            field: "activeTo",
            valueGetter: (value) => value && new Date(value),
            type: "date",
            filterOperators: dateFilterOperators,
            width: 180,
          },
          availableBenefitsEnabled && {
            headerName: __("Aktywny"),
            field: "dynamicallyActivePerk",
            type: "boolean",
            width: 120,
          },
          {
            headerName: __("Grupy pracownicze"),
            field: "employeeGroups",
            type: "custom",
            renderCell: ({ value }) => mapValueToOptions(value, employeeGroups),
            renderHeaderFilter: (params) => (
              <MultiSelectFilter {...params} options={employeeGroups} />
            ),
            width: 200,
          },
          {
            headerName: __("Grupy dochodowości"),
            field: "rentableGroups",
            type: "custom",
            renderCell: ({ value }) => mapValueToOptions(value, rentableGroups),
            renderHeaderFilter: (params) => (
              <MultiSelectFilter {...params} options={rentableGroups} />
            ),
            width: 200,
          },
          {
            headerName: __("Jednostki organizacyjne"),
            field: "organizationUnits",
            type: "custom",
            renderCell: ({ value }) =>
              mapValueToOptions(value, organizationUnits),
            renderHeaderFilter: (params) => (
              <MultiSelectFilter {...params} options={organizationUnits} />
            ),
            width: 200,
          },
          {
            headerName: __("Kolejność wyświetlania"),
            field: "sort",
            type: "string",
            filterOperators: stringFilterOperators,
            width: 110,
          },
          {
            headerName: __("Kategoria"),
            field: "category.id",
            valueGetter: (_value, row) => row.category.id,
            type: "singleSelect",
            filterOperators: selectFilterOperators,
            valueOptions: perkCategoriesOptions,
            renderCell: (param) => (
              <div className="text-truncate" title={param.formattedValue}>
                {param.formattedValue}
              </div>
            ),
            width: 200,
          },
          {
            headerName: __("Finansowy"),
            field: "financial",
            type: "boolean",
            width: 120,
          },
          {
            headerName: __("Akcja"),
            field: "actions",
            type: "actions",
            minWidth: 60,
            getActions: (params) => [
              <>
                {canEdit && (
                  <IconButton
                    component={RouterLink}
                    title={__("Edytuj ")}
                    to={`${pathPrefix}/total-reward/perks/${params.row.id}`}
                  >
                    <Preview />
                  </IconButton>
                )}
              </>,
              <>
                {canDelete && <RowActionDeletePerk perkId={params.row.id} />}
              </>,
            ],
          },
        ] as GridColDef<TotalRewardsPerk>[]
      ).filter(Boolean),
    [
      availableBenefitsEnabled,
      canDelete,
      canEdit,
      dateFilterOperators,
      employeeGroups,
      organizationUnits,
      pathPrefix,
      perkCategoriesOptions,
      rentableGroups,
      selectFilterOperators,
      stringFilterOperators,
      yourBenefitsEnabled,
    ]
  );

  const language = getLanguage();

  const getRowHeight = useCallback(({ model }: GridRowHeightParams) => {
    if (
      model.employeeGroups.length ||
      model.rentableGroups.length ||
      model.organizationUnits.length
    ) {
      return "auto";
    }
    return null;
  }, []);

  const apiRef = useGridApiRef();

  const handleReset = useCallback(() => {
    apiRef.current?.setFilterModel({ items: [] });
  }, [apiRef]);

  return (
    <ThemeProvider theme={DefaultTheme({ language })}>
      <Paper
        sx={{
          padding: "1rem",
        }}
      >
        <Stack
          direction="row"
          sx={{ justifyContent: "flex-end", gap: 2, mb: 1 }}
        >
          {canCreate && (
            <Button
              component={RouterLink}
              variant="contained"
              startIcon={<Add />}
              to={`${pathPrefix}/total-reward/perks/-1`}
            >
              {__("Dodaj benefit")}
            </Button>
          )}
          {canExport && <ExportPerks />}
          <Button
            variant="outlined"
            color="error"
            startIcon={<FilterListOff />}
            onClick={handleReset}
          >
            {__("Resetuj filtry")}
          </Button>
        </Stack>
        {isError && (
          <Alert variant="outlined" color="warning">
            {String(error)}
          </Alert>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: 400,
          }}
        >
          <DataGrid
            columns={columns}
            getRowId={(row) => row.id}
            rows={data?.member || []}
            rowCount={rowCount}
            loading={isFetching}
            initialState={{
              pinnedColumns: { left: [], right: ["actions"] },
            }}
            getRowHeight={getRowHeight}
            disableColumnMenu
            disableColumnReorder
            disableColumnSelector
            disableDensitySelector
            disableRowSelectionOnClick
            disableVirtualization
            disableColumnFilter
            disableEval
            headerFilters
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[5, 10, 25, 50, 100]}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            filterMode="server"
            filterModel={filterModel}
            onFilterModelChange={setFilterModel}
            slotProps={{
              basePagination: {
                material: {
                  ActionsComponent: Pagination,
                },
              },
              loadingOverlay: {
                variant: "linear-progress",
                noRowsVariant: "circular-progress",
              },
            }}
            sx={{
              [`& .${gridClasses.cell}`]: {
                alignContent: "center",
              },
            }}
          />
        </Box>
      </Paper>
    </ThemeProvider>
  );
};

export default PerksListing;
